/**
 * Pulsing ring rendered over a tour-target element via portal.
 * Re-measures on scroll/resize so it tracks the element.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

interface Rect { top: number; left: number; width: number; height: number }

export function TourBeacon({ targetEl }: { targetEl: HTMLElement | null }): JSX.Element | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!targetEl) { setRect(null); return; }
    const measure = () => {
      const r = targetEl.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true, capture: true });
    window.addEventListener('resize', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('scroll', measure, { capture: true });
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [targetEl]);

  if (!rect) return null;

  const size = 32;
  const style: React.CSSProperties = {
    position: 'fixed',
    top: rect.top + rect.height / 2 - size / 2,
    left: rect.left + rect.width / 2 - size / 2,
    width: size, height: size,
    zIndex: 9998, pointerEvents: 'none',
  };

  return createPortal(
    <span style={style} aria-hidden>
      <span className={cn('absolute inset-0 rounded-full bg-brand-400 opacity-30 animate-ping')} />
      <span className="absolute inset-[8px] rounded-full bg-brand-400" />
    </span>,
    document.body,
  );
}
